import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  BookOpenCheck,
  Compass,
  Eye,
  Flame,
  GraduationCap,
  HandHeart,
  HeartPulse,
  Landmark,
  MoveRight,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Sun,
  Target,
  Users,
  Waves,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import { lineageMoments } from "../data/globalPresence";

const programIconMap = {
  HeartPulse,
  GraduationCap,
  HandHeart,
  ShieldPlus: ShieldCheck,
  ShieldCheck,
  BookOpenCheck,
  MonitorCog: GraduationCap,
};

const EMPTY_CONFIG = {};

const configuredImage = (item) => item?.image || item?.imageUrl || item?.src;

const configuredAlt = (item, fallback) =>
  item?.alt || item?.imageAlt || item?.title || item?.label || fallback;

const dedupeBySrc = (items) => {
  const seen = new Set();

  return items.filter((item) => {
    const src = item?.src;
    if (!src || seen.has(src)) return false;
    seen.add(src);
    return true;
  });
};

const asClosingGalleryImage = (item) => ({
  src: configuredImage(item),
  srcMobile: item?.srcMobile || item?.imageMobile || "",
  label: item?.label || item?.caption || item?.eyebrow || item?.title || "Mission field image",
  alt: configuredAlt(item, "AGM mission image"),
});

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const floatingTerms = [
  "Atman",
  "Brahman",
  "Jnana",
  "Seva",
  "Advaita",
  "Dharma",
  "Shraddha",
  "Karuna",
  "Samatva",
  "Lokasangraha",
];

const shankaraPath = [
  "Upanishads",
  "Bhagavad Gita",
  "Brahma Sutra",
  "Adi Shankaracharya",
  "Living Vedant",
  "AGM Seva",
];

const sourceWall = [
  "Isha Upanishad 1",
  "Katha Upanishad 2.1.1",
  "Mundaka Upanishad 1.1.4-5",
  "Mandukya Upanishad 2 and 7",
  "Aitareya Upanishad 3.3",
  "Brihadaranyaka Upanishad 1.4.10 and 2.3.6",
  "Chandogya Upanishad 6.8.7",
  "Bhagavad Gita 2.48, 2.50, 3.20-25, 5.18, 6.29, 12.13",
  "Brahma Sutra 1.1.1-1.1.2",
  "Adi Shankaracharya's Prasthana Trayi commentarial lineage",
];

const imageCredits = [
  {
    label: "Adi Shankaracharya statue at Kedarnath",
    credit: "Mayank7078, Wikimedia Commons, CC BY-SA 4.0",
    href: "https://commons.wikimedia.org/wiki/File:Shankaracharya.jpg",
  },
  {
    label: "Omkareshwar and the Narmada",
    credit: "Shampa Chaturvedi, Wikimedia Commons, CC BY-SA 4.0",
    href: "https://commons.wikimedia.org/wiki/File:Omkareshwar_-_Narmada.jpg",
  },
  {
    label: "Adi Shankaracharya with disciples",
    credit: "Wikimedia Commons, CC BY-SA licensed image",
    href: "https://commons.wikimedia.org/wiki/File:Adi_Shankaracharya_with_Disciples.jpg",
  },
  {
    label: "Omkareshwar temple at night",
    credit: "Pushkaraj.yogi, Wikimedia Commons, CC BY 3.0",
    href: "https://commons.wikimedia.org/wiki/File:Omkareshwar_Temple.png",
  },
];

const closingFallbackImages = [
  {
    src: "/assets/agm/vedant-reference/adi-shankaracharya-disciples.jpg",
    label: "Teaching lineage",
    alt: "Adi Shankaracharya with disciples",
  },
  {
    src: "/assets/agm/vedant-reference/omkareshwar-temple-night.jpg",
    label: "Sacred geography",
    alt: "Omkareshwar temple at night",
  },
  {
    src: "/assets/agm/polyclinic.jpg",
    label: "Healthcare seva",
    alt: "AGM healthcare program",
  },
  {
    src: "/assets/agm/ifs-lab.jpg",
    label: "Future skilling",
    alt: "Institute of Future Skilling computer lab",
  },
];

const Vedant = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();
  const [activeGate, setActiveGate] = useState(0);
  const pageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const vedant = siteConfig.vedant || EMPTY_CONFIG;
  const swamiji = siteConfig.swamiji || EMPTY_CONFIG;

  const sourceStreams = [
    {
      icon: BookOpenCheck,
      title: "Upanishads",
      source: "Sruti",
      text: t("vedant.sourceStreams.upanishads.text"),
    },
    {
      icon: Target,
      title: "Bhagavad Gita",
      source: "Smriti",
      text: t("vedant.sourceStreams.gita.text"),
    },
    {
      icon: Compass,
      title: "Brahma Sutra",
      source: "Nyaya",
      text: t("vedant.sourceStreams.brahmaSutra.text"),
    },
  ];

  const sacredVisuals = [
    {
      eyebrow: "Adi Shankaracharya",
      title: t("vedant.sacredVisuals.shankaracharya.title"),
      text: t("vedant.sacredVisuals.shankaracharya.text"),
      image: "/assets/agm/vedant-reference/shankaracharya-kedarnath-hero.jpg",
      alt: t("vedant.sacredVisuals.shankaracharya.alt"),
    },
    {
      eyebrow: "Jnana bhumi",
      title: t("vedant.sacredVisuals.omkareshwar.title"),
      text: t("vedant.sacredVisuals.omkareshwar.text"),
      image: "/assets/agm/vedant-reference/omkareshwar-narmada-wide.jpg",
      alt: t("vedant.sacredVisuals.omkareshwar.alt"),
    },
    {
      eyebrow: t("vedant.sacredVisuals.swamiji.eyebrow"),
      title: t("vedant.sacredVisuals.swamiji.title"),
      text: t("vedant.sacredVisuals.swamiji.text"),
      image: "/assets/agm/swamiji-vedant.jpg",
      alt: t("vedant.sacredVisuals.swamiji.alt"),
    },
  ];

  const vedantToSeva = [
    {
      icon: Sun,
      title: t("vedant.vedantToSeva.ishaVision.title"),
      phrase: "Ishavasyam idam sarvam",
      source: "Isha Upanishad 1",
      learning: t("vedant.vedantToSeva.ishaVision.learning"),
      agm: t("vedant.vedantToSeva.ishaVision.agm"),
      takeHome: t("vedant.vedantToSeva.ishaVision.takeHome"),
      image: "/assets/agm/vedant-reference/omkareshwar-narmada-wide.jpg",
    },
    {
      icon: Eye,
      title: t("vedant.vedantToSeva.innerSeeing.title"),
      phrase: "Paranci khani vyatrinat svayambhuh",
      source: "Katha Upanishad 2.1.1",
      learning: t("vedant.vedantToSeva.innerSeeing.learning"),
      agm: t("vedant.vedantToSeva.innerSeeing.agm"),
      takeHome: t("vedant.vedantToSeva.innerSeeing.takeHome"),
      image: "/assets/agm/swamiji-vedant.jpg",
    },
    {
      icon: Flame,
      title: t("vedant.vedantToSeva.higherKnowledge.title"),
      phrase: "Para vidya",
      source: "Mundaka Upanishad 1.1.4-5",
      learning: t("vedant.vedantToSeva.higherKnowledge.learning"),
      agm: t("vedant.vedantToSeva.higherKnowledge.agm"),
      takeHome: t("vedant.vedantToSeva.higherKnowledge.takeHome"),
      image: "/assets/agm/ifs-lab.jpg",
    },
    {
      icon: Waves,
      title: t("vedant.vedantToSeva.nonDualDignity.title"),
      phrase: "Sama-darshinah",
      source: "Bhagavad Gita 5.18",
      learning: t("vedant.vedantToSeva.nonDualDignity.learning"),
      agm: t("vedant.vedantToSeva.nonDualDignity.agm"),
      takeHome: t("vedant.vedantToSeva.nonDualDignity.takeHome"),
      image: "/assets/agm/mass-wedding-dignitaries.jpg",
    },
  ];

  const mahavakyas = [
    {
      phrase: "Prajnanam Brahma",
      meaning: t("vedant.mahavakyas.prajnanam.meaning"),
      source: "Aitareya Upanishad 3.3",
      agm: t("vedant.mahavakyas.prajnanam.agm"),
    },
    {
      phrase: "Aham Brahmasmi",
      meaning: t("vedant.mahavakyas.aham.meaning"),
      source: "Brihadaranyaka Upanishad 1.4.10",
      agm: t("vedant.mahavakyas.aham.agm"),
    },
    {
      phrase: "Tat Tvam Asi",
      meaning: t("vedant.mahavakyas.tatTvam.meaning"),
      source: "Chandogya Upanishad 6.8.7",
      agm: t("vedant.mahavakyas.tatTvam.agm"),
    },
    {
      phrase: "Ayam Atma Brahma",
      meaning: t("vedant.mahavakyas.ayam.meaning"),
      source: "Mandukya Upanishad 2",
      agm: t("vedant.mahavakyas.ayam.agm"),
    },
  ];

  const gitaToNGO = [
    {
      icon: ShieldCheck,
      title: t("vedant.gitaToNGO.steadyAction.title"),
      phrase: "Samatvam yoga ucyate",
      source: "Bhagavad Gita 2.48",
      teaching: t("vedant.gitaToNGO.steadyAction.teaching"),
      ngo: t("vedant.gitaToNGO.steadyAction.ngo"),
    },
    {
      icon: Target,
      title: t("vedant.gitaToNGO.professionalSeva.title"),
      phrase: "Yogah karmasu kaushalam",
      source: "Bhagavad Gita 2.50",
      teaching: t("vedant.gitaToNGO.professionalSeva.teaching"),
      ngo: t("vedant.gitaToNGO.professionalSeva.ngo"),
    },
    {
      icon: Users,
      title: t("vedant.gitaToNGO.publicWelfare.title"),
      phrase: "Lokasangraha",
      source: "Bhagavad Gita 3.20-25",
      teaching: t("vedant.gitaToNGO.publicWelfare.teaching"),
      ngo: t("vedant.gitaToNGO.publicWelfare.ngo"),
    },
    {
      icon: HandHeart,
      title: t("vedant.gitaToNGO.compassion.title"),
      phrase: "Adveshta sarva-bhutanam",
      source: "Bhagavad Gita 12.13",
      teaching: t("vedant.gitaToNGO.compassion.teaching"),
      ngo: t("vedant.gitaToNGO.compassion.ngo"),
    },
  ];

  const swamiChangeModel = [
    {
      icon: HeartPulse,
      title: t("vedant.swamiChange.healthcare.title"),
      source: "Bhagavad Gita 6.29",
      text: t("vedant.swamiChange.healthcare.text"),
      image: "/assets/agm/polyclinic-consultation.jpg",
    },
    {
      icon: GraduationCap,
      title: t("vedant.swamiChange.education.title"),
      source: "Mundaka Upanishad 1.1.4-5",
      text: t("vedant.swamiChange.education.text"),
      image: "/assets/agm/ifs-students.jpg",
    },
    {
      icon: HandHeart,
      title: t("vedant.swamiChange.womenDignity.title"),
      source: "Bhagavad Gita 5.18",
      text: t("vedant.swamiChange.womenDignity.text"),
      image: "/assets/agm/mass-wedding-ceremony.jpg",
    },
    {
      icon: ShieldCheck,
      title: t("vedant.swamiChange.reliefSeva.title"),
      source: "Bhagavad Gita 3.20-25",
      text: t("vedant.swamiChange.reliefSeva.text"),
      image: "/assets/agm/mahakumbh-medical.jpg",
    },
  ];

  const takeHomePractices = [
    t("vedant.takeHome.dignity"),
    t("vedant.takeHome.donating"),
    t("vedant.takeHome.learning"),
    t("vedant.takeHome.judging"),
    t("vedant.takeHome.leading"),
  ];

  const configuredPrinciples = useMemo(
    () => (Array.isArray(vedant.principles) ? vedant.principles.filter(Boolean) : []),
    [vedant.principles]
  );
  const heroTitle = vedant.title || t("vedant.hero.title");
  const heroDescription = vedant.quote || t("vedant.hero.description");
  const heroBackdrop = {
    image: vedant.heroImage || sacredVisuals[0].image,
    alt: vedant.heroImageAlt || sacredVisuals[0].alt,
  };
  const sacredVisualCards = useMemo(
    () =>
      sacredVisuals.map((item, index) => {
        if (index !== 2) return item;

        return {
          ...item,
          eyebrow: swamiji.eyebrow || item.eyebrow,
          title: swamiji.title || vedant.title || item.title,
          text: swamiji.description || vedant.quote || item.text,
          image: swamiji.image || vedant.image || item.image,
          alt: swamiji.title || configuredAlt(vedant, item.alt),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [swamiji, vedant]
  );
  const vedantSevaCards = useMemo(
    () =>
      vedantToSeva.map((item, index) => ({
        ...item,
        agm: configuredPrinciples[index] || item.agm,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configuredPrinciples]
  );
  const swamiChangeCards = useMemo(() => {
    const programs = Array.isArray(siteConfig.programPillars) ? siteConfig.programPillars : [];
    const programsById = new Map(programs.map((program) => [program.id, program]));
    const preferredProgramIds = ["healthcare", "future-skilling", "mass-wedding", "mahakumbh"];

    return preferredProgramIds.map((id, index) => {
      const fallback = swamiChangeModel[index];
      const program = programsById.get(id);
      if (!program) return fallback;

      return {
        ...fallback,
        icon: programIconMap[program.icon] || fallback.icon,
        title: program.title || fallback.title,
        source: program.eyebrow || fallback.source,
        text: program.description || fallback.text,
        image: program.image || fallback.image,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteConfig.programPillars]);
  const closingGallery = useMemo(() => {
    const configuredGallery = Array.isArray(siteConfig.gallery)
      ? siteConfig.gallery.map(asClosingGalleryImage)
      : [];

    return dedupeBySrc([
      closingFallbackImages[0],
      closingFallbackImages[1],
      ...configuredGallery,
      ...closingFallbackImages,
    ]).slice(0, 4);
  }, [siteConfig.gallery]);
  const takeHomeList = configuredPrinciples.length
    ? configuredPrinciples.slice(0, 5)
    : takeHomePractices;
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ["start start", "end end"],
  });

  const progressHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const heroY = useTransform(scrollYProgress, [0, 0.25], ["0%", "8%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = window.setInterval(() => {
      setActiveGate((current) => (current + 1) % vedantSevaCards.length);
    }, 3200);

    return () => window.clearInterval(timer);
  }, [reduceMotion, vedantSevaCards.length]);

  const active = vedantSevaCards[activeGate % vedantSevaCards.length];

  return (
    <main ref={pageRef} className="overflow-x-hidden bg-[#fbf7f1] text-[#241511]">
      <motion.div
        aria-hidden="true"
        className="fixed left-5 top-28 z-40 hidden h-[58vh] w-px bg-[#eadfd6] lg:block"
      >
        <motion.div className="w-px bg-[#7f1916]" style={{ height: progressHeight }} />
      </motion.div>

      <section className="relative isolate min-h-[100svh] overflow-hidden bg-[#fbf7f1] pt-24 lg:pt-28">
        <motion.img
          src={heroBackdrop.image}
          alt={heroBackdrop.alt}
          className="absolute inset-0 h-full w-full object-cover object-[58%_center]"
          style={{ y: reduceMotion ? 0 : heroY }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(251,247,241,0.98)_0%,rgba(251,247,241,0.92)_41%,rgba(251,247,241,0.58)_66%,rgba(75,36,24,0.12)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_24%,rgba(127,25,22,0.12),transparent_26%),linear-gradient(90deg,rgba(127,25,22,0.06)_1px,transparent_1px)] bg-[length:100%_100%,96px_96px]" />

        <motion.div
          aria-hidden="true"
          className="absolute inset-x-0 top-28 flex whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.7em] text-[#7f1916]/18"
          animate={reduceMotion ? undefined : { x: ["0%", "-38%"] }}
          transition={{ duration: 42, repeat: Infinity, ease: "linear" }}
        >
          {[...floatingTerms, ...floatingTerms, ...floatingTerms].map((term, index) => (
            <span key={`${term}-${index}`} className="mx-8">
              {term}
            </span>
          ))}
        </motion.div>

        <div className="container relative z-10 mx-auto flex min-h-[calc(100svh-6rem)] items-end px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <motion.div className="max-w-5xl" style={{ y: reduceMotion ? 0 : heroY }}>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mb-5 inline-flex items-center gap-3 border border-[#7f1916]/20 bg-white/58 px-4 py-2 backdrop-blur"
            >
              <Sparkles className="h-4 w-4 text-[#7f1916]" />
              <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
                {t("vedant.hero.eyebrow")}
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.08 }}
              className="max-w-5xl text-4xl font-semibold leading-[0.96] tracking-normal text-[#241511] md:text-7xl xl:text-8xl"
            >
              {heroTitle}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.18 }}
              className="mt-7 max-w-3xl text-lg leading-8 text-[#4f3a31] md:text-xl"
            >
              {heroDescription}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.26 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <a
                href="#vedant-journey"
                className="inline-flex items-center justify-center gap-3 bg-[#7f1916] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#641210]"
              >
                {t("vedant.hero.enterJourney")}
                <MoveRight size={18} />
              </a>
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-3 border border-[#4b2418]/35 bg-white/62 px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#241511] backdrop-blur transition hover:bg-white"
              >
                {t("vedant.hero.seeSevaExpression")}
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="mt-12 grid gap-4 border-t border-[#7f1916]/18 pt-6 sm:grid-cols-3"
            >
              {sourceStreams.map(({ icon: Icon, title, source, text }) => (
                <motion.div key={title} variants={fadeUp} className="pr-4">
                  <Icon className="h-5 w-5 text-[#7f1916]" />
                  <p className="mt-4 text-sm font-semibold text-[#241511]">{title}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[#7d6a5f]">
                    {source}
                  </p>
                  <p className="mt-3 text-sm leading-6 text-[#5f4a3e]">{text}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 right-6 hidden max-w-xs border border-white/45 bg-white/64 p-4 text-xs leading-6 text-[#4f3a31] shadow-xl shadow-[#4b2418]/10 backdrop-blur lg:block">
          <p className="font-bold uppercase tracking-[0.2em] text-[#7f1916]">
            {t("vedant.sidebar.heading")}
          </p>
          <p className="mt-2">
            {vedant.quote || t("vedant.sidebar.quote")}
          </p>
        </div>
      </section>

      <section id="vedant-journey" className="bg-white py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-4xl text-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
              {t("vedant.lineage.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
              {t("vedant.lineage.heading")}
            </h2>
            <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
              {t("vedant.lineage.description")}
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mt-16 grid gap-5 lg:grid-cols-3"
          >
            {sacredVisualCards.map((item) => (
              <motion.article
                key={item.title}
                variants={fadeUp}
                whileHover={reduceMotion ? undefined : { y: -6 }}
                className="group overflow-hidden border border-[#eadfd6] bg-[#fbf7f1]"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#7f1916]">
                    {item.eyebrow}
                  </p>
                  <h3 className="mt-4 text-2xl font-semibold leading-tight text-[#241511]">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>

          <div className="mt-16 grid gap-8 border-t border-[#eadfd6] pt-10 lg:grid-cols-[0.32fr_0.68fr] lg:items-center">
            <div>
              <Landmark className="h-9 w-9 text-[#7f1916]" />
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.24em] text-[#7f1916]">
                {t("vedant.lineagePath.eyebrow")}
              </p>
              <h3 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-4xl">
                {t("vedant.lineagePath.heading")}
              </h3>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid gap-3 md:grid-cols-3"
            >
              {shankaraPath.map((step, index) => (
                <motion.div
                  key={step}
                  variants={fadeUp}
                  className="relative border border-[#eadfd6] bg-white p-5"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                    0{index + 1}
                  </span>
                  <p className="mt-4 text-lg font-semibold leading-snug text-[#241511]">{step}</p>
                  {index < shankaraPath.length - 1 && (
                    <MoveRight className="absolute -right-4 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-[#7f1916] md:block" />
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-[#fffaf5] py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-4xl text-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
              {t("vedant.guruParampara.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
              {t("vedant.guruParampara.heading")}
            </h2>
            <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
              {t("vedant.guruParampara.description")}
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4"
          >
            {lineageMoments.map((item) => (
              <motion.article
                key={item.title}
                variants={fadeUp}
                whileHover={reduceMotion ? undefined : { y: -6 }}
                className="group overflow-hidden border border-[#eadfd6] bg-white shadow-sm"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    style={{ objectPosition: item.objectPosition || "center" }}
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7f1916]">
                    {item.eyebrow}
                  </p>
                  <h3 className="mt-4 text-xl font-semibold leading-tight text-[#241511]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/global-presence"
              className="inline-flex items-center justify-center gap-3 bg-[#4b2418] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
            >
              {t("vedant.guruParampara.cta")}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.38fr_0.62fr] lg:items-start">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="lg:sticky lg:top-28"
            >
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
                {t("vedant.perspectives.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
                {t("vedant.perspectives.heading")}
              </h2>
              <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
                {t("vedant.perspectives.description")}
              </p>
            </motion.div>

            <div>
              <div className="grid gap-3 md:grid-cols-4">
                {vedantSevaCards.map((item, index) => (
                  <button
                    key={item.title}
                    type="button"
                    onClick={() => setActiveGate(index)}
                    className={`border p-4 text-left transition ${
                      activeGate === index
                        ? "border-[#7f1916] bg-[#7f1916] text-white"
                        : "border-[#eadfd6] bg-white text-[#241511] hover:border-[#7f1916]"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <p className="mt-4 text-sm font-semibold">{item.title}</p>
                    <p
                      className={`mt-1 text-xs ${activeGate === index ? "text-white/70" : "text-[#7d6a5f]"}`}
                    >
                      {item.source}
                    </p>
                  </button>
                ))}
              </div>

              <motion.article
                key={active.title}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="mt-5 grid overflow-hidden border border-[#eadfd6] bg-white shadow-xl shadow-[#4b2418]/8 lg:grid-cols-[0.48fr_0.52fr]"
              >
                <img
                  src={active.image}
                  alt={active.title}
                  className="h-full min-h-[420px] w-full object-cover"
                />
                <div className="p-7 md:p-10">
                  <p className="text-sm font-semibold text-[#7f1916]">{active.phrase}</p>
                  <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-[#7d6a5f]">
                    {active.source}
                  </p>
                  <h3 className="mt-6 text-3xl font-semibold leading-tight text-[#241511] md:text-4xl">
                    {active.title}
                  </h3>

                  <div className="mt-8 space-y-5">
                    {[
                      [t("vedant.perspectives.labelTeaches"), active.learning],
                      [t("vedant.perspectives.labelExpresses"), active.agm],
                      [t("vedant.perspectives.labelTakeHome"), active.takeHome],
                    ].map(([label, text]) => (
                      <div key={label} className="border-l-2 border-[#7f1916] pl-4">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                          {label}
                        </p>
                        <p className="mt-2 text-sm leading-7 text-[#5f4a3e]">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.article>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid gap-12 lg:grid-cols-[0.43fr_0.57fr]"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
                {t("vedant.swamiChange.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
                {t("vedant.swamiChange.heading")}
              </h2>
              <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
                {t("vedant.swamiChange.description")}
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {swamiChangeCards.map(({ icon: Icon, ...item }) => (
                <motion.article
                  key={item.title}
                  whileHover={reduceMotion ? undefined : { y: -6 }}
                  className="overflow-hidden border border-[#eadfd6] bg-[#fbf7f1]"
                >
                  <img src={item.image} alt={item.title} className="h-52 w-full object-cover" />
                  <div className="p-5">
                    <Icon className="h-6 w-6 text-[#7f1916]" />
                    <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-[#7d6a5f]">
                      {item.source}
                    </p>
                    <h3 className="mt-3 text-xl font-semibold leading-snug text-[#241511]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#7f1916] py-20 text-white lg:py-28">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="container relative z-10 mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.38fr_0.62fr] lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#f4e4d0]">
              {t("vedant.mahavakyas.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-6xl">
              {t("vedant.mahavakyas.heading")}
            </h2>
            <p className="mt-5 text-base leading-8 text-white/76">
              {t("vedant.mahavakyas.description")}
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid gap-4 md:grid-cols-2"
          >
            {mahavakyas.map((item) => (
              <motion.article
                key={item.phrase}
                variants={fadeUp}
                className="border border-white/16 bg-white/10 p-6 backdrop-blur"
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f4e4d0]">
                  {item.source}
                </p>
                <h3 className="mt-5 text-3xl font-semibold">{item.phrase}</h3>
                <p className="mt-2 text-sm font-semibold text-white/62">{item.meaning}</p>
                <p className="mt-5 text-sm leading-7 text-white/76">{item.agm}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.42fr_0.58fr]">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="lg:sticky lg:top-28"
            >
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
                {t("vedant.gitaToNGO.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
                {t("vedant.gitaToNGO.heading")}
              </h2>
              <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
                {t("vedant.gitaToNGO.description")}
              </p>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid gap-4 md:grid-cols-2"
            >
              {gitaToNGO.map(({ icon: Icon, ...item }) => (
                <motion.article
                  key={item.title}
                  variants={fadeUp}
                  className="border border-[#eadfd6] bg-white p-6 shadow-sm"
                >
                  <Icon className="h-7 w-7 text-[#7f1916]" />
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-[#7d6a5f]">
                    {item.source}
                  </p>
                  <h3 className="mt-4 text-2xl font-semibold text-[#241511]">{item.title}</h3>
                  <p className="mt-3 text-sm font-semibold text-[#7f1916]">{item.phrase}</p>
                  <p className="mt-4 text-sm leading-7 text-[#5f4a3e]">{item.teaching}</p>
                  <p className="mt-4 border-t border-[#eadfd6] pt-4 text-sm leading-7 text-[#5f4a3e]">
                    {item.ngo}
                  </p>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#4b2418] py-20 text-white lg:py-28">
        <div className="container mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.52fr_0.48fr] lg:items-center lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2">
            {closingGallery.map((image, index) => (
              <motion.div
                key={image.src}
                className={`${index % 2 ? "sm:translate-y-10" : ""} group overflow-hidden border border-white/15 bg-white/10`}
                whileHover={reduceMotion ? undefined : { scale: 1.03 }}
              >
                <div className="relative">
                  <picture>
                    {image.srcMobile ? (
                      <source srcSet={image.srcMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="h-64 w-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </picture>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#241511]/82 to-transparent p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f4e4d0]">
                      {image.label}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#f4e4d0]">
              {t("vedant.takeHome.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-6xl">
              {t("vedant.takeHome.heading")}
            </h2>
            <div className="mt-8 space-y-3">
              {takeHomeList.map((practice, index) => (
                <div key={practice} className="flex gap-4 border border-white/14 bg-white/8 p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-[#f4e4d0] text-sm font-bold text-[#241511]">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-7 text-white/78">{practice}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 text-[#241511] lg:py-20">
        <div className="container mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[0.5fr_0.5fr] lg:items-center lg:px-8">
          <div>
            <ScrollText className="h-8 w-8 text-[#7f1916]" />
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
              {t("vedant.sourceDiscipline.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-5xl">
              {t("vedant.sourceDiscipline.heading")}
            </h2>
            <p className="mt-5 text-sm leading-7 text-[#5f4a3e]">
              {t("vedant.sourceDiscipline.description")}
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {sourceWall.map((source) => (
              <div
                key={source}
                className="border border-[#eadfd6] bg-[#fbf7f1] px-4 py-3 text-sm text-[#5f4a3e]"
              >
                {source}
              </div>
            ))}
          </div>
          <div className="lg:col-span-2">
            <div className="mt-4 border-t border-[#eadfd6] pt-5">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#7f1916]">
                {t("vedant.imageCredits.heading")}
              </p>
              <div className="mt-3 grid gap-2 text-xs leading-6 text-[#6b594f] md:grid-cols-2">
                {imageCredits.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="transition hover:text-[#7f1916]"
                  >
                    {item.label}: {item.credit}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#fbf7f1] py-16 lg:py-20">
        <div className="container mx-auto flex flex-col gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7f1916]">
              {t("vedant.cta.eyebrow")}
            </p>
            <h2 className="mt-3 max-w-4xl text-3xl font-semibold leading-tight text-[#241511] md:text-5xl">
              {t("vedant.cta.heading")}
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/programs"
              className="inline-flex items-center justify-center gap-3 bg-[#4b2418] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#3a1a12]"
            >
              {t("vedant.cta.seeSevaPrograms")}
              <MoveRight size={18} />
            </Link>
            <Link
              to={region.donationPath}
              className="inline-flex items-center justify-center gap-3 bg-[#7f1916] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#641210]"
            >
              {region.paymentLabel}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Vedant;
